import { OfficesApi } from "../api";
import { MapType } from "../model/mapModels";
import L from "leaflet";
import { GeoPackageStyle } from "../utils/geopackage-style-utils";

export class MapUtils {
  mapObject: L.Map | null = null;
  additionalLayerGroups: Record<string, L.LayerGroup> = {};
  legend: L.Control | null = null;
  chosenMapType: MapType | null = null;
  isMapReady = false;
  geopackageLayers: Array<{
    id: string;
    name: string;
    visible: boolean;
    style?: GeoPackageStyle;
    layerId?: number;
    geometryType: string;
    imported?: boolean; // True if layer was imported through the import wizard
  }> = [];
  layerDisplayNames = {};

  constructor() {}

  // getCurrentLocation (state) {
  //   return this.currentLocation;
  // }
  getLayerGroups() {
    return this.additionalLayerGroups;
  }
  getChosenMapType() {
    return this.chosenMapType;
  }
  getMapObject() {
    return this.mapObject;
  }
  getLegend() {
    return this.legend;
  }

  setMapObject(map: L.Map) {
    this.mapObject = map;
    this.isMapReady = true;
  }
  async waitForMap(): Promise<boolean> {
    const start = Date.now();
    let attempts = 0;
    const maxAttempts = 10;

    while (!this.isMapReady && attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }
    return this.isMapReady;
  }
  async fitMapToBounds(bounds: L.LatLngBounds) {
    if (!bounds || !bounds.isValid()) {
      console.warn("Invalid bounds provided");
      return;
    }

    const isReady = await this.waitForMap();
    if (!isReady || !this.mapObject) {
      console.warn("Map not ready for bounds fitting");
      return;
    }

    try {
      // Force a reflow of the map container
      this.mapObject.invalidateSize(true);

      // Wait for container to be ready
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Check if map is still valid
      if (!this.mapObject || !this.mapObject.getContainer()) {
        console.warn("Map container lost during bounds fitting");
        return;
      }

      // Fit bounds with conservative options
      this.mapObject.fitBounds(bounds, {
        padding: [30, 30],
        maxZoom: 10,
        animate: false,
        duration: 0,
      });
    } catch (error) {
      console.error("Error in fitMapToBounds:", error);
    }
  }

  async addLayerGroup(
    id: string,
    layerGroup: L.LayerGroup | L.GeoJSON,
    displayName?: string,
    style?: GeoPackageStyle,
    fitBounds: boolean = true,
    imported: boolean = false, // Flag to indicate if layer was imported through wizard
  ) {
    const isReady = await this.waitForMap();
    if (!isReady || !this.mapObject) {
      console.warn("Map not ready for adding layer");
      return false;
    }

    try {
      // Remove existing layer if present
      this.removeLayerGroup(id);

      // Wait for removal to complete
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Set display name on the layer for controls/legends if provided
      if (displayName) {
        layerGroup.options = layerGroup.options || {};
        (layerGroup.options as any).name = displayName;
      }

      // Add new layer
      layerGroup.addTo(this.mapObject);
      this.additionalLayerGroups[id] = layerGroup;

      // Save the display name in a property for later use
      if (displayName) {
        // If we have a dedicated property for display names, use it
        // Otherwise add it as a custom property
        this.layerDisplayNames = this.layerDisplayNames || {};
        this.layerDisplayNames[id] = displayName;
      }

      // If this is a GeoPackage layer, track it
      if (id.startsWith("geopackage-")) {
        // Check if we already have this layer
        const existingIndex = this.geopackageLayers.findIndex(
          (l) => l.id === id,
        );

        if (existingIndex >= 0) {
          // Update existing layer
          this.geopackageLayers[existingIndex].visible = true;
          if (style) {
            this.geopackageLayers[existingIndex].style = style;
          }
        } else {
          // Extract layerId from id (format: geopackage-{layerId}-{displayName})
          const layerIdMatch = id.match(/^geopackage-(\d+)-/);
          const extractedLayerId = layerIdMatch
            ? parseInt(layerIdMatch[1])
            : undefined;

          // Add new layer
          this.geopackageLayers.push({
            id,
            name: displayName || id.replace("geopackage-", ""),
            visible: true,
            style: style || {},
            layerId: extractedLayerId,
            // Try to detect geometry type from the layer
            geometryType: this.detectGeometryType(layerGroup),
            imported: imported, // Mark if layer was imported through wizard
          });
        }
      }

      // Calculate bounds from all layers in the group (only if fitBounds is true)
      if (fitBounds) {
        const bounds = this.calculateLayerGroupBounds(layerGroup);
        if (bounds && bounds.isValid()) {
          await this.fitMapToBounds(bounds);
        }
      }

      return true;
    } catch (error) {
      console.error(`Error adding layer group "${displayName || id}":`, error);
      return false;
    }
  }

  calculateLayerGroupBounds(layerGroup: L.LayerGroup): L.LatLngBounds | null {
    try {
      let bounds: L.LatLngBounds | null = null;

      layerGroup.eachLayer((layer: any) => {
        // Get bounds based on layer type
        let layerBounds: L.LatLngBounds | undefined;

        if (layer.getBounds) {
          // For circles, polygons, etc.
          layerBounds = layer.getBounds();
        } else if (layer.getLatLng) {
          // For markers, etc.
          const latLng = layer.getLatLng();
          layerBounds = L.latLngBounds([latLng, latLng]);
        }

        if (layerBounds && layerBounds.isValid()) {
          if (bounds === null) {
            bounds = layerBounds;
          } else {
            bounds.extend(layerBounds);
          }
        }
      });

      return bounds;
    } catch (error) {
      console.error("Error calculating layer group bounds:", error);
      return null;
    }
  }
  removeLayerGroup(name: string) {
    if (!this.mapObject) return;

    try {
      const layer = this.additionalLayerGroups[name];
      if (layer) {
        this.mapObject.removeLayer(layer);
        delete this.additionalLayerGroups[name];

        // Also remove from geopackageLayers array
        const geoLayerIndex = this.geopackageLayers.findIndex(
          (l) => l.id === name,
        );
        if (geoLayerIndex !== -1) {
          this.geopackageLayers.splice(geoLayerIndex, 1);
        }

        // Clean up layer display name
        delete this.layerDisplayNames[name];
      }
    } catch (error) {
      console.error("Error removing layer:", error);
    }
  }
  removeLegend() {
    if (this.legend && this.mapObject) {
      this.legend.remove();
      this.legend = null;
    }
  }
  addLegend(legend: L.Control) {
    if (this.mapObject) {
      this.legend = legend;
      legend.addTo(this.mapObject);
    }
  }
  setChosenMapType(mapType: MapType) {
    this.chosenMapType = mapType;
  }
  async fetchCurrentLocation() {
    new OfficesApi();
  }
  addControl(control: L.Control) {
    if (this.mapObject) {
      control.addTo(this.mapObject);
    }
  }
  removeControl(control: L.Control) {
    if (this.mapObject) {
      control.remove();
    }
  }
  /**
   * Update the style of a GeoPackage layer
   */
  async updateGeoPackageLayerStyle(layerId: string, style: GeoPackageStyle) {

    // Find the layer in geopackageLayers
    const layer = this.geopackageLayers.find((l) => l.id === layerId);
    if (!layer) {
      console.warn("[MapUtils] Layer not found in geopackageLayers:", layerId);
      return false;
    }


    // Update the style in local state
    layer.style = style;

    // Get the layer instance - FIXED: use additionalLayerGroups instead of getLayerGroups
    const layerInstance = this.additionalLayerGroups[layerId];
    if (!layerInstance) {
      console.warn(
        "[MapUtils] Layer instance not found in additionalLayerGroups:",
        layerId,
      );
      return false;
    }


    try {
      // Extract actual GeoJSON layer - this assumes your layer groups may contain GeoJSON layers
      if (layerInstance instanceof L.LayerGroup) {
        // Apply style to all sublayers
        layerInstance.eachLayer((sublayer) => {
          if (sublayer instanceof L.GeoJSON) {
            // First, update the style option
            sublayer.options.style = () => style;

            // Then, apply the style to all existing features
            sublayer.eachLayer((feature: any) => {
              if (feature instanceof L.Path) {
                // For paths (polygons, lines), build a clean Leaflet-compatible style object
                const leafletStyle: any = {};

                // For lines, if fillColor is set (from editor), use it as the line color
                // The editor sets the selected color to fillColor for all geometry types
                if (feature instanceof L.Polyline && !(feature instanceof L.Polygon)) {
                  // This is a LineString - use fillColor as color if available
                  leafletStyle.color = style.fillColor || style.color || '#3388ff';
                } else {
                  // This is a Polygon - use both color (outline) and fillColor (fill)
                  if (style.color) leafletStyle.color = style.color;
                  if (style.fillColor) leafletStyle.fillColor = style.fillColor;
                  if (style.fillOpacity !== undefined) leafletStyle.fillOpacity = style.fillOpacity;
                }

                if (style.weight !== undefined) leafletStyle.weight = style.weight;
                if (style.opacity !== undefined) leafletStyle.opacity = style.opacity;
                if (style.dashArray) leafletStyle.dashArray = style.dashArray;

                feature.setStyle(leafletStyle);
              } else if (feature instanceof L.Marker) {
                // For markers, we need to handle icon changes
                const latlng = feature.getLatLng();
                const featureData = (feature as any).feature;

                // Remove old marker
                sublayer.removeLayer(feature);

                // Create new marker with updated style
                let newMarker: L.Marker | L.CircleMarker;

                if (style.icon) {
                  // Custom icon marker
                  const apiBaseUrl =
                    import.meta.env.VITE_API_BASE_URL || "/jmapi/v1";

                  // Check if it's an icon ID (starts with "icon_") or a file path
                  let iconUrl: string;
                  if (style.icon.startsWith("icon_")) {
                    // Database-stored icon
                    iconUrl = `${apiBaseUrl}/geopackage/icon/${style.icon}`;
                  } else {
                    // Filesystem icon
                    iconUrl = `${apiBaseUrl}/geopackage/icon-file?icon_path=${encodeURIComponent(style.icon)}`;
                  }

                  // For custom icons, radius is already in pixels
                  const iconSize = style.radius || 32;

                  const customIcon = L.icon({
                    iconUrl: iconUrl,
                    iconSize: [iconSize, iconSize],
                    iconAnchor: [iconSize / 2, iconSize / 2],
                    popupAnchor: [0, -iconSize / 2],
                  });

                  newMarker = L.marker(latlng, { icon: customIcon });
                } else {
                  // Circle marker (default)
                  newMarker = L.circleMarker(latlng, {
                    radius: style.radius || 8,
                    fillColor: style.fillColor || style.color || "#3388ff",
                    color: style.color || "#3388ff",
                    weight: style.weight || 2,
                    opacity: style.opacity !== undefined ? style.opacity : 1,
                    fillOpacity:
                      style.fillOpacity !== undefined ? style.fillOpacity : 0.5,
                  });
                }

                // Preserve feature data and popup if exists
                if (featureData) {
                  (newMarker as any).feature = featureData;
                }
                if (feature.getPopup()) {
                  newMarker.bindPopup(
                    feature.getPopup()!.getContent() as string,
                  );
                }

                // Add new marker to sublayer
                sublayer.addLayer(newMarker);
              }
            });
          }
        });
      } else if (layerInstance instanceof L.GeoJSON) {
        // Direct GeoJSON layer - handle the same way
        layerInstance.options.style = () => style;

        layerInstance.eachLayer((feature: any) => {
          if (feature instanceof L.Path) {
            // For paths (polygons, lines), build a clean Leaflet-compatible style object
            const leafletStyle: any = {};

            // For lines, if fillColor is set (from editor), use it as the line color
            // The editor sets the selected color to fillColor for all geometry types
            if (feature instanceof L.Polyline && !(feature instanceof L.Polygon)) {
              // This is a LineString - use fillColor as color if available
              leafletStyle.color = style.fillColor || style.color || '#3388ff';
            } else {
              // This is a Polygon - use both color (outline) and fillColor (fill)
              if (style.color) leafletStyle.color = style.color;
              if (style.fillColor) leafletStyle.fillColor = style.fillColor;
              if (style.fillOpacity !== undefined) leafletStyle.fillOpacity = style.fillOpacity;
            }

            if (style.weight !== undefined) leafletStyle.weight = style.weight;
            if (style.opacity !== undefined) leafletStyle.opacity = style.opacity;
            if (style.dashArray) leafletStyle.dashArray = style.dashArray;

            feature.setStyle(leafletStyle);
          } else if (feature instanceof L.Marker) {
            // Recreate marker with new style (same logic as above)
            const latlng = feature.getLatLng();
            const featureData = feature.feature;

            layerInstance.removeLayer(feature);

            let newMarker: L.Marker | L.CircleMarker;

            if (style.icon) {
              const apiBaseUrl =
                import.meta.env.VITE_API_BASE_URL || "/jmapi/v1";
              const iconUrl = `${apiBaseUrl}/geopackage/icon/${style.icon}`;
              // For custom icons, radius is already in pixels
              const iconSize = style.radius || 32;

              const customIcon = L.icon({
                iconUrl: iconUrl,
                iconSize: [iconSize, iconSize],
                iconAnchor: [iconSize / 2, iconSize / 2],
                popupAnchor: [0, -iconSize / 2],
              });

              newMarker = L.marker(latlng, { icon: customIcon });
            } else {
              newMarker = L.circleMarker(latlng, {
                radius: style.radius || 8,
                fillColor: style.fillColor || style.color || "#3388ff",
                color: style.color || "#3388ff",
                weight: style.weight || 2,
                opacity: style.opacity !== undefined ? style.opacity : 1,
                fillOpacity:
                  style.fillOpacity !== undefined ? style.fillOpacity : 0.5,
              });
            }

            if (featureData) {
              (newMarker as any).feature = featureData;
            }
            if (feature.getPopup()) {
              newMarker.bindPopup(feature.getPopup()!.getContent() as string);
            }

            layerInstance.addLayer(newMarker);
          }
        });
      }

      // Style changes are only applied locally to the map
      // We do NOT persist these changes to the backend

      return true;
    } catch (error) {
      console.error("Error updating layer style:", error);
      return false;
    }
  }
  detectGeometryType(layer: L.LayerGroup | L.GeoJSON): string {
    try {
      let geometryType = "Unknown";

      // Check for GeoJSON layer
      if (layer instanceof L.GeoJSON) {
        // Look at the features
        layer.eachLayer((feature) => {
          if (feature instanceof L.Marker) {
            geometryType = "Point";
            return false; // break the loop
          } else if (
            feature instanceof L.Polyline &&
            !(feature instanceof L.Polygon)
          ) {
            geometryType = "LineString";
            return false;
          } else if (feature instanceof L.Polygon) {
            geometryType = "Polygon";
            return false;
          }
        });
      } else if (layer instanceof L.LayerGroup) {
        // Check sublayers
        layer.eachLayer((sublayer) => {
          if (sublayer instanceof L.GeoJSON) {
            // Recurse
            const subType = this.detectGeometryType(sublayer);
            if (subType !== "Unknown") {
              geometryType = subType;
              return false;
            }
          } else if (sublayer instanceof L.Marker) {
            geometryType = "Point";
            return false;
          } else if (
            sublayer instanceof L.Polyline &&
            !(sublayer instanceof L.Polygon)
          ) {
            geometryType = "LineString";
            return false;
          } else if (sublayer instanceof L.Polygon) {
            geometryType = "Polygon";
            return false;
          }
        });
      }

      return geometryType;
    } catch (error) {
      console.error("Error detecting geometry type:", error);
      return "Unknown";
    }
  }
}
